import React, { useState } from "react";
import { RotateCcw, Volume2, Piano } from "lucide-react";
import { PianoKeyboard } from "@/components/PianoKeyboard";
import { playSequence } from "@/lib/audio";

const ORDER = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT = { "C#": "D♭", "D#": "E♭", "F#": "G♭", "G#": "A♭", "A#": "B♭" };
const midi = (p) => Number(p.slice(-1)) * 12 + ORDER.indexOf(p.slice(0, -1));
export const keyName = (p) => { const n = p.slice(0, -1); return FLAT[n] ? `${n[0]}♯/${FLAT[n]}` : n; };
export const verdict = (a, b) => {
  const d = Math.abs(midi(a) - midi(b));
  if (d === 0) return { size: "same", text: "That's the same key twice. Tap a different second key." };
  if (d === 1) return { size: "semitone", text: "A semitone — next-door keys with nothing in between." };
  if (d === 2) return { size: "tone", text: "A tone — two semitones, with one key in between." };
  return { size: "bigger", text: `${d} semitones apart — bigger than a tone. Try keys closer together.` };
};

// Lesson 5: tap two keys and find out whether they are a tone or a semitone apart.
export const ToneTapper = ({ testId = "tone-tapper" }) => {
  const [keys, setKeys] = useState([]);
  const press = (p) => {
    const next = keys.length >= 2 ? [p] : [...keys, p];
    setKeys(next);
    if (next.length === 2) setTimeout(() => playSequence(next, 0.5, 0.45), 450);
  };
  const v = keys.length === 2 ? verdict(keys[0], keys[1]) : null;
  const tone = v && (v.size === "semitone" || v.size === "tone");
  const hi = Object.fromEntries(keys.map((k, i) => [k, i ? "2" : "1"]));

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Piano size={14} /> Tone tapper</p>
        {keys.length > 0 && <button type="button" onClick={() => setKeys([])} data-testid={`${testId}-reset`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><RotateCcw size={12} /> Start again</button>}
      </div>
      <p className="mt-2 text-sm text-muted-foreground" data-testid={`${testId}-prompt`}>{keys.length === 0 ? "Tap any key, then tap a second key." : keys.length === 1 ? `First key: ${keyName(keys[0])}. Now tap a second key.` : "Tap another key to start a new pair."}</p>
      <div className="mt-3 overflow-x-auto">
        <div className="min-w-[30rem]"><PianoKeyboard start={3} octaves={2} highlight={hi} onPress={press} testId={`${testId}-keyboard`} /></div>
      </div>
      {v && (
        <div className={`mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 p-3 ${tone ? "border-emerald-500 bg-emerald-500/10" : "border-border"}`} data-testid={`${testId}-result`} data-size={v.size}>
          <p className="text-sm font-semibold">
            <span data-testid={`${testId}-pair`}>{keyName(keys[0])} → {keyName(keys[1])}</span>: <span className={tone ? "text-emerald-600 dark:text-emerald-400" : ""}>{v.text}</span>
          </p>
          <button type="button" onClick={() => playSequence(keys, 0.5, 0.45)} data-testid={`${testId}-replay`} className="inline-flex items-center gap-1.5 rounded-full border border-primary px-3 py-1 text-xs font-bold text-primary"><Volume2 size={12} /> Hear them again</button>
        </div>
      )}
    </div>
  );
};
